import React, { useState, useEffect, useCallback } from 'react';
import { Spin, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import dayjs from 'dayjs';
import useBackRestore, { useScrollRestore } from '../hooks/useBackRestore';

const GOLD = '#B8892A';
const BD = '#D8D2C8';
const BD2 = '#EAE6DF';
const BG = '#FAF8F5';
const BG2 = '#F4F1EC';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#9B2335';
const GRN = '#2A6645';
const BLUE = '#1A4F8C';

// 已结束委托在列表中的保留天数（详情页深链仍可访问）
const ENDED_RETENTION_DAYS = 30;

const STATUS_TABS = [
  { key: 'active',   label: '竞拍中', filter: 'approved,queued,executing' },
  { key: 'watching', label: '待出价', filter: '' },
  { key: 'outbid',   label: '已被超', filter: '' },
  { key: 'won',      label: '已中标', filter: 'won' },
  { key: 'unwon',    label: '未中标', filter: 'lost,cancelled,rejected,bid_failed' },
];

const SORT_OPTIONS = [
  { key: 'end_at:asc', label: '剩余时间' },
  { key: 'max_bid_jpy:desc', label: '出价金额' },
  { key: 'created_at:desc', label: '投标时间' },
];

/* ── Countdown hook ── */
function useCountdown(endAt) {
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  useEffect(() => {
    if (!endAt) { setText(''); return; }
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('已结束'); setUrgent(false); return; }
      setUrgent(diff < 3 * 3600);
      const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600),
        m = Math.floor((diff % 3600) / 60), s = diff % 60;
      if (d > 0) setText(`${d}天${h}小时${m}分${s}秒`);
      else if (h > 0) setText(`${h}小时${m}分${s}秒`);
      else setText(`${m}分${s}秒`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endAt]);
  return { text, urgent };
}

const STRATEGY_LABELS = {
  immediate: '立即出价',
  scheduled: '预约出价',
  manual: '手动出价',
};
const STRATEGY_COLORS = {
  immediate: { color: BLUE, bg: 'rgba(26,79,140,.08)' },
  scheduled: { color: '#8C5A0A', bg: 'rgba(140,90,10,.08)' },
  manual: { color: '#6B3FA0', bg: 'rgba(107,63,160,.08)' },
};

/* 拍卖行出价反馈（源站错误码 → 前端友好提示）——让用户看到"为什么没出上价" */
const BID_WARN = '#C77700';
const BID_ERROR_LABELS = {
  BID_TOO_LOW:      { tag: '出价过低', hint: '低于拍卖行最低加价，请提高出价上限后再试' },
  BID_NOT_ALIGNED:  { tag: '价格不符', hint: '出价不符合拍卖行加价单位，请调整出价上限' },
  BID_REJECTED:     { tag: '出价被拒', hint: '拍卖行拒绝了本次出价，请查看详情' },
  AUCTION_ENDED:    { tag: '已结束',   hint: '该拍品已结束，无法出价' },
  LOGIN_FAILED:     { tag: '暂不可出价', hint: '拍卖行账号连接异常，请稍后再试或联系客服' },
  DRY_RUN_NOT_SENT: { tag: '通道开通中', hint: '该拍卖行的出价通道正在开通中' },
  RUNNER_ERROR:     { tag: '出价异常', hint: '出价执行异常，系统将自动重试' },
  RESULT_UNKNOWN:   { tag: '结果待确认', hint: '出价结果待确认，请稍后查看' },
};
function getBidError(m) {
  const code = m.bid_error_code;
  const placed = Number(m.last_bid_jpy || 0) > 0;
  if (!code || placed) return null;
  const meta = BID_ERROR_LABELS[code] || { tag: '出价未成功', hint: '本次出价未成功，请查看详情' };
  // 源站原文末尾可能带 |MIN_BID:95000 机器可读后缀（komehyo 精确最低价），显示时剥掉
  return { ...meta, src: String(m.bid_error_msg || '').replace(/\s*\|MIN_BID:\d+/g, '').trim() };
}

/* 从 bid_error_msg 解析源站给的精确最低可出价（|MIN_BID:95000 后缀）→ 用于改价预填 */
function parseMinBid(msg) {
  const mt = /\|MIN_BID:(\d+)/.exec(String(msg || ''));
  return mt ? Number(mt[1]) : null;
}

/* ── BidCard ── */
function BidCard({ mandate, onAdjust, onOpenLot, onManualSubmit, isMobile }) {
  const { text: countdown, urgent } = useCountdown(mandate.end_at);
  const isLeading = !!mandate.is_leading;
  const isActive = ['approved', 'queued', 'executing'].includes(mandate.status);
  const isEnded = ['won', 'lost', 'cancelled', 'rejected', 'bid_failed'].includes(mandate.status);
  const currentPrice = Number(mandate.lot_current_price || 0);
  const myBid = Number(mandate.max_bid_jpy || 0);
  // 铁律:当前价 > 我的上限 ⇒ 必然已被超（代理出价也不可能超过我的上限）。
  // is_leading 取自「上次出价那刻」会过时（出价后别人加价、当前价涨过上限，标记仍是 true），
  // 故当前价可见时以价格为准，优先于过期的 is_leading，避免误显「暂时领先」+ 误藏「加价」按钮。
  const priceOutbid = currentPrice > 0 && currentPrice > myBid;
  const leadingNow = isLeading && !priceOutbid;

  let images = [];
  try { images = typeof mandate.lot_image_urls === 'string' ? JSON.parse(mandate.lot_image_urls) : (mandate.lot_image_urls || []); } catch {}
  const thumb = images[0] || '';

  const statusCfg = {
    pending_review: { label: '待审核', color: '#8C5A0A', bg: 'rgba(140,90,10,.1)' },
    approved: { label: '竞拍中', color: BLUE, bg: 'rgba(26,79,140,.1)' },
    queued: { label: '出价中', color: BLUE, bg: 'rgba(26,79,140,.1)' },
    executing: { label: '执行中', color: BLUE, bg: 'rgba(26,79,140,.1)' },
    won: { label: '已中标', color: GRN, bg: 'rgba(42,102,69,.1)' },
    lost: { label: '未中标', color: INK4, bg: BG2 },
    cancelled: { label: '已取消', color: INK4, bg: BG2 },
    rejected: { label: '已拒绝', color: RED, bg: 'rgba(155,35,53,.08)' },
    bid_failed: { label: '出价失败', color: RED, bg: 'rgba(155,35,53,.08)' },
  };
  const st = statusCfg[mandate.status] || { label: mandate.status, color: INK4, bg: BG2 };

  const bidErr = getBidError(mandate);   // 拍卖行拒绝/未成功的反馈（源站→前端）
  // 失败后可一键改价重新出价：bid_failed，或有拍卖行错误反馈且未中标、场次未结束
  // （后端已支持对 bid_failed 委托 adjust-bid：自动复位 approved 并重新出价，保证金按新价重冻）
  const sessionEnded = mandate.end_at && dayjs(mandate.end_at).isBefore(dayjs());
  const canRearm = !sessionEnded &&
    (mandate.status === 'bid_failed' || (!!mandate.bid_error_code && mandate.status !== 'won' && !!bidErr));
  // 领先 / 被超 / 出价未成功 状态（出价未成功优先，避免把被拒的出价误显示成有效）
  const bidStatus = !isActive ? null
    : bidErr ? { label: bidErr.tag, color: BID_WARN, bg: `${BID_WARN}14` }
    : priceOutbid ? { label: '已被超', color: RED, bg: 'rgba(155,35,53,.08)' }
    : leadingNow ? { label: '暂时领先', color: GRN, bg: 'rgba(42,102,69,.12)' }
    : null;

  return (
    <div style={{
      background: '#fff', borderRadius: 10, border: `1px solid ${BD2}`,
      borderLeft: `4px solid ${leadingNow ? GRN : bidStatus?.color === RED ? RED : st.color}`,
      marginBottom: 10, overflow: 'hidden',
      transition: 'box-shadow .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(26,20,16,.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', gap: 12, padding: '12px 14px' }}>
        {/* 缩略图 */}
        <div
          onClick={() => onOpenLot(mandate.lot_id)}
          style={{
            width: isMobile ? 72 : 80, height: isMobile ? 72 : 80, borderRadius: 8, overflow: 'hidden',
            background: BG2, flexShrink: 0, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {thumb
            ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 32 }}>📦</span>
          }
        </div>

        {/* 信息区 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 标题 */}
          <div
            onClick={() => onOpenLot(mandate.lot_id)}
            style={{
              fontSize: 13, fontWeight: 500, color: INK, lineHeight: 1.4, cursor: 'pointer',
              overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              marginBottom: 6,
            }}
          >
            {mandate.title_zh || mandate.title_ja || '暂无标题'}
          </div>

          {/* 标签行 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: st.bg, color: st.color, fontWeight: 600 }}>
              {st.label}
            </span>
            {bidStatus && (
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: bidStatus.bg, color: bidStatus.color, fontWeight: 600 }}>
                {bidStatus.label}
              </span>
            )}
            {mandate.submit_strategy && mandate.submit_strategy !== 'immediate' && (() => {
              const sc = STRATEGY_COLORS[mandate.submit_strategy] || STRATEGY_COLORS.immediate;
              return (
                <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: sc.bg, color: sc.color, fontWeight: 600 }}>
                  {STRATEGY_LABELS[mandate.submit_strategy] || mandate.submit_strategy}
                  {!mandate.bid_submitted && mandate.submit_before_mins ? ` · ${mandate.submit_before_mins}分` : ''}
                </span>
              );
            })()}
            {mandate.house_name && (
              <span style={{ fontSize: 10, color: INK4 }}>{mandate.house_name}</span>
            )}
            {mandate.session_name && (
              <span style={{ fontSize: 10, color: INK4 }}>| {mandate.session_name}</span>
            )}
          </div>

          {/* 委托编号 & 出价次数 */}
          <div style={{ fontSize: 10, color: INK4, marginBottom: 4 }}>
            {mandate.mandate_no}
            {Number(mandate.bid_attempt_count) > 0 && (
              <span style={{ marginLeft: 8 }}>出价{mandate.bid_attempt_count}次</span>
            )}
            {mandate.start_price_jpy > 0 && (
              <span style={{ marginLeft: 8 }}>起拍¥{Number(mandate.start_price_jpy).toLocaleString('ja-JP')}</span>
            )}
          </div>

          {/* 价格行 */}
          <div style={{ display: 'flex', gap: isMobile ? 10 : 20, flexWrap: 'wrap', fontSize: 12 }}>
            <div>
              <span style={{ color: INK4 }}>当前价：</span>
              <span style={{ fontFamily: 'var(--mono,monospace)', fontWeight: 600, color: currentPrice > myBid && isActive ? RED : INK }}>
                {currentPrice > 0 ? `¥${currentPrice.toLocaleString('ja-JP')}` : '—'}
              </span>
            </div>
            <div>
              <span style={{ color: INK4 }}>我的出价：</span>
              <span style={{ fontFamily: 'var(--mono,monospace)', fontWeight: 600, color: GOLD }}>
                ¥{myBid.toLocaleString('ja-JP')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 拍卖行出价反馈：告诉用户"为什么没出上价"（源站→前端） */}
      {(isActive || mandate.status === 'bid_failed') && bidErr && (
        <div style={{ padding: '8px 14px', background: `${BID_WARN}12`, borderTop: `1px solid ${BD2}` }}>
          <div style={{ fontSize: 12, color: BID_WARN, lineHeight: 1.5, fontWeight: 600 }}>⚠ {bidErr.hint}</div>
          {!!bidErr.src && (
            <div style={{ marginTop: 3, fontSize: 11, color: INK4, lineHeight: 1.4 }}>拍卖行反馈：{bidErr.src}</div>
          )}
        </div>
      )}

      {/* 底部操作栏 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderTop: `1px solid ${BD2}`, background: BG,
        gap: 8, flexWrap: 'wrap',
      }}>
        {/* 倒计时 */}
        <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          {!isEnded && countdown ? (
            <>
              <span style={{ color: INK4 }}>剩余：</span>
              <span style={{ fontWeight: 600, color: urgent ? RED : INK2, fontFamily: 'var(--mono,monospace)' }}>
                {countdown}
              </span>
            </>
          ) : isEnded ? (
            <span style={{ color: INK4 }}>
              {mandate.status === 'won' ? `中标价 ¥${Number(mandate.won_price_jpy || 0).toLocaleString('ja-JP')}` : '已结束'}
            </span>
          ) : (
            <span style={{ color: INK4 }}>—</span>
          )}
        </div>

        {/* 操作按钮 */}
        <div style={{ display: 'flex', gap: 6 }}>
          {/* 出价失败/被拒：一键改价重新委托（不必重建委托，保证金按新价重冻） */}
          {canRearm && (
            <button
              onClick={() => onAdjust(mandate)}
              style={{
                padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                background: BID_WARN, color: '#fff', border: 'none', cursor: 'pointer',
              }}
            >
              调整出价重新委托
            </button>
          )}
          {/* 手动出价：未提交时显示"立即出价"按钮 */}
          {isActive && mandate.submit_strategy === 'manual' && !mandate.bid_submitted && (
            <button
              onClick={() => onManualSubmit && onManualSubmit(mandate)}
              style={{
                padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                background: '#6B3FA0', color: '#fff', border: 'none', cursor: 'pointer',
              }}
            >
              立即出价
            </button>
          )}
          {/* 预约出价：未提交时显示等待提示 */}
          {isActive && mandate.submit_strategy === 'scheduled' && !mandate.bid_submitted && (
            <span style={{ fontSize: 11, color: '#8C5A0A', fontWeight: 600, padding: '5px 0' }}>
              等待提交
            </span>
          )}
          {isActive && !canRearm && !leadingNow && (mandate.bid_submitted || mandate.submit_strategy === 'immediate' || !mandate.submit_strategy) && (
            <button
              onClick={() => onAdjust(mandate)}
              style={{
                padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                background: RED, color: '#fff', border: 'none', cursor: 'pointer',
              }}
            >
              加价
            </button>
          )}
          {isActive && leadingNow && (
            <span style={{ fontSize: 11, color: GRN, fontWeight: 600, padding: '5px 0' }}>
              暂时领先
            </span>
          )}
          <button
            onClick={() => onOpenLot(mandate.lot_id)}
            style={{
              padding: '5px 14px', borderRadius: 5, fontSize: 12,
              background: '#fff', color: INK3, border: `1px solid ${BD}`, cursor: 'pointer',
            }}
          >
            查看
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── WatchlistCard ── */
function WatchlistCard({ lot, onOpenLot, onBid, isMobile }) {
  const { text: countdown, urgent } = useCountdown(lot.end_at);
  const currentPrice = Number(lot.current_price_jpy || 0);
  const startPrice = Number(lot.start_price_jpy || 0);
  // 2026-07-04: 时间感知——end_at 已过即视为结束(与倒计时口径一致),
  // 防 status 未回写时关注卡仍显示可点出价按钮。end_at 存在性守卫必留。
  const isEnded = lot.status === 'ended' || lot.status === 'sold' || lot.status === 'closed' ||
    (lot.end_at && dayjs(lot.end_at).isBefore(dayjs()));

  let images = [];
  try { images = typeof lot.image_urls === 'string' ? JSON.parse(lot.image_urls) : (lot.image_urls || []); } catch {}
  const thumb = images[0] || '';

  const hasPending = lot.mandate && ['pending_review', 'pending'].includes(lot.mandate.status);

  return (
    <div style={{
      background: '#fff', borderRadius: 10, border: `1px solid ${BD2}`,
      borderLeft: `4px solid ${hasPending ? '#8C5A0A' : GOLD}`,
      marginBottom: 10, overflow: 'hidden',
      transition: 'box-shadow .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(26,20,16,.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', gap: 12, padding: '12px 14px' }}>
        <div
          onClick={() => onOpenLot(lot.id)}
          style={{
            width: isMobile ? 72 : 80, height: isMobile ? 72 : 80, borderRadius: 8, overflow: 'hidden',
            background: BG2, flexShrink: 0, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {thumb
            ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 32 }}>📦</span>
          }
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            onClick={() => onOpenLot(lot.id)}
            style={{
              fontSize: 13, fontWeight: 500, color: INK, lineHeight: 1.4, cursor: 'pointer',
              overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              marginBottom: 6,
            }}
          >
            {lot.title_zh || lot.title_ja || '暂无标题'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
              background: hasPending ? 'rgba(140,90,10,.1)' : 'rgba(184,137,42,.08)',
              color: hasPending ? '#8C5A0A' : GOLD,
            }}>
              {hasPending ? '待审核' : '关注中'}
            </span>
            {lot.house_name && (
              <span style={{ fontSize: 10, color: INK4 }}>{lot.house_name}</span>
            )}
            {lot.session_name && (
              <span style={{ fontSize: 10, color: INK4 }}>| {lot.session_name}</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: isMobile ? 10 : 20, flexWrap: 'wrap', fontSize: 12 }}>
            <div>
              <span style={{ color: INK4 }}>当前价：</span>
              <span style={{ fontFamily: 'var(--mono,monospace)', fontWeight: 600, color: INK }}>
                {currentPrice > 0 ? `¥${currentPrice.toLocaleString('ja-JP')}` : '—'}
              </span>
            </div>
            {startPrice > 0 && (
              <div>
                <span style={{ color: INK4 }}>起拍：</span>
                <span style={{ fontFamily: 'var(--mono,monospace)', color: INK3 }}>
                  ¥{startPrice.toLocaleString('ja-JP')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderTop: `1px solid ${BD2}`, background: BG,
        gap: 8, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          {!isEnded && countdown ? (
            <>
              <span style={{ color: INK4 }}>剩余：</span>
              <span style={{ fontWeight: 600, color: urgent ? RED : INK2, fontFamily: 'var(--mono,monospace)' }}>
                {countdown}
              </span>
            </>
          ) : (
            <span style={{ color: INK4 }}>已结束</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {!isEnded && !hasPending && (
            <button
              onClick={() => onBid(lot.id)}
              style={{
                padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                background: GOLD, color: '#fff', border: 'none', cursor: 'pointer',
              }}
            >
              出价
            </button>
          )}
          {hasPending && (
            <span style={{ fontSize: 11, color: '#8C5A0A', fontWeight: 600, padding: '5px 0' }}>
              审核中
            </span>
          )}
          <button
            onClick={() => onOpenLot(lot.id)}
            style={{
              padding: '5px 14px', borderRadius: 5, fontSize: 12,
              background: '#fff', color: INK3, border: `1px solid ${BD}`, cursor: 'pointer',
            }}
          >
            查看
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── 按拍品去重：同一 lot_id 只保留最高出价的委托 ── */
function dedup(mandates) {
  const map = new Map();
  for (const m of mandates) {
    const key = m.lot_id;
    const existing = map.get(key);
    if (!existing || Number(m.max_bid_jpy || 0) > Number(existing.max_bid_jpy || 0)) {
      map.set(key, m);
    }
  }
  return Array.from(map.values());
}

/* ══ Main Page ══ */
export default function BiddingCenter() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 返回恢复:从拍品详情(/lots/:id 路由页)back 回来时恢复 tab/排序/滚动
  const [restored, saveListState] = useBackRestore();
  const [mandates, setMandates] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => restored?.tab || 'active');
  const [sort, setSort] = useState(() => restored?.sort || 'end_at:asc');
  useEffect(() => { saveListState({ tab, sort }); }, [saveListState, tab, sort]);
  useScrollRestore(restored, saveListState, !loading);
  const [counts, setCounts] = useState({ active: 0, watching: 0, outbid: 0, won: 0, unwon: 0 });

  // 加价弹窗
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustMinBid, setAdjustMinBid] = useState(null);   // 已知的最低可出价（源站 MIN_BID / 后端 400 返回）
  const [adjusting, setAdjusting] = useState(false);

  // 打开改价弹窗；出价失败/被拒的委托预填：源站 MIN_BID → 当前价 → 现出价上限
  const openAdjust = (mandate) => {
    const minBid = parseMinBid(mandate.bid_error_msg);
    setAdjustMinBid(minBid);
    if (mandate.status === 'bid_failed' || (mandate.bid_error_code && Number(mandate.last_bid_jpy || 0) <= 0)) {
      const cur = Number(mandate.lot_current_price || 0);
      const prefill = minBid || (cur > 0 ? cur : Number(mandate.max_bid_jpy) || 0);
      setAdjustValue(prefill ? String(prefill) : '');
    } else {
      setAdjustValue('');
    }
    setAdjustTarget(mandate);
  };

  const fetchMandates = useCallback(async () => {
    setLoading(true);
    try {
      // 并行获取统计 + 关注列表
      const [statsRes, watchRes] = await Promise.all([
        api.get('/mandates/my-stats'),
        api.get('/watchlist'),
      ]);
      const stats = statsRes.data;
      const wList = (watchRes.data.data || []).filter(w => {
        // 排除已有活跃委托的关注项（它们会出现在竞拍中 tab）
        if (w.mandate && ['approved', 'queued', 'executing'].includes(w.mandate.status)) return false;
        // 排除已结束拍品（status 或 end_at 已过）
        if (w.status === 'ended' || w.status === 'sold') return false;
        if (w.end_at && dayjs(w.end_at).isBefore(dayjs())) return false;
        return true;
      });
      setWatchlist(wList);

      // 待出价 = 待审核委托数 + 关注（未出价）数
      const pendingCount = Number(stats.pending || 0);
      const watchingNoBid = wList.filter(w => !w.mandate).length;
      const watchingCount = pendingCount + watchingNoBid;

      // 始终获取活跃委托来计算 outbid 数量（不受当前 tab 影响）
      const activeRes = await api.get('/mandates', { params: { status: 'approved,queued,executing', limit: 200 } });
      const activeAll = dedup(activeRes.data.data || activeRes.data);
      // 当前价 > 我的上限 = 必然已被超（价格铁证，优先于可能过期的 is_leading；
      // 原来 && !m.is_leading 会因标记过时而漏计，导致「已被超」数与卡片状态不一致）。
      const outbidList = activeAll.filter(m =>
        Number(m.lot_current_price || 0) > 0 &&
        Number(m.lot_current_price || 0) > Number(m.max_bid_jpy || 0)
      );

      setCounts({
        active: Number(stats.active || 0),
        watching: watchingCount,
        outbid: outbidList.length,
        won:   Number(stats.won || 0),
        unwon: Number(stats.lost || 0) + Number(stats.cancelled || 0) + Number(stats.rejected || 0) + Number(stats.failed || 0),
      });

      // 获取列表
      if (tab === 'watching') {
        // 待出价 tab: 待审核的委托 + 关注中的拍品
        // 已结束 lot 上的 pending_review 委托不属于"可操作的待出价"——这里前端兜底过滤掉，
        // 防止 admin 漏审/审到一半场次结束的委托卡在"待出价"里误导用户。
        const pendingRes = await api.get('/mandates', { params: { status: 'pending_review', sort, limit: 100 } });
        const now = Date.now();
        const pendingList = (pendingRes.data.data || []).filter(m => {
          if (!m.end_at) return true;
          return new Date(m.end_at).getTime() > now;
        });
        setMandates(dedup(pendingList));
      } else if (tab === 'outbid') {
        setMandates(outbidList);
      } else if (tab === 'active') {
        // 复用已获取的活跃委托数据
        setMandates(activeAll);
      } else {
        const tabCfg = STATUS_TABS.find(t => t.key === tab);
        const params = { sort, limit: 100 };
        if (tabCfg?.filter) params.status = tabCfg.filter;
        // 已中标/未中标 tab：只保留最近 ENDED_RETENTION_DAYS 天内创建的委托，
        // 老订单不在列表显示，详情页深链仍可访问。
        if (tab === 'won' || tab === 'unwon') {
          const since = new Date(Date.now() - ENDED_RETENTION_DAYS * 86400_000);
          params.date_from = since.toISOString().slice(0, 10);
        }
        const { data } = await api.get('/mandates', { params });
        const list = dedup(data.data || data);
        setMandates(list);
      }
    } catch (e) {
      message.error('获取委托列表失败');
    } finally {
      setLoading(false);
    }
  }, [tab, sort]);

  useEffect(() => { fetchMandates(); }, [fetchMandates]);

  // 30秒自动刷新
  useEffect(() => {
    const timer = setInterval(fetchMandates, 30000);
    return () => clearInterval(timer);
  }, [fetchMandates]);

  const handleAdjust = async () => {
    if (!adjustTarget || !adjustValue) return;
    const newBid = Number(adjustValue);
    if (isNaN(newBid) || newBid <= 0) { message.error('请输入有效金额'); return; }
    // 方向校验交给后端：暗拍结拍前可升可降，明拍仅可加价（后端按 bid_type 判定并返回提示）
    // bid_failed 允许同价重新提交（后端复位 approved 重新出价）
    if (adjustTarget.status !== 'bid_failed' && newBid === Number(adjustTarget.max_bid_jpy)) {
      message.error('出价未变化'); return;
    }
    setAdjusting(true);
    try {
      const { data } = await api.patch(`/mandates/${adjustTarget.id}/adjust-bid`, { max_bid_jpy: newBid });
      message.success(data?.rearmed ? '已提交，将自动重新委托' : '出价已调整');
      setAdjustTarget(null);
      setAdjustValue('');
      setAdjustMinBid(null);
      fetchMandates();
    } catch (e) {
      const d = e?.response?.data;
      message.error(d?.error || '调整失败');
      // 400 带 min_bid_jpy：把弹窗预填更新为后端给的最低价，用户直接再确认即可
      if (d?.min_bid_jpy) {
        setAdjustMinBid(Number(d.min_bid_jpy));
        setAdjustValue(String(d.min_bid_jpy));
      }
    } finally {
      setAdjusting(false);
    }
  };

  // 手动出价提交
  const handleManualSubmit = async (mandate) => {
    Modal.confirm({
      title: '确认立即出价',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>{mandate.title_zh || mandate.title_ja}</div>
          <div>授权价格：¥{Number(mandate.max_bid_jpy).toLocaleString('ja-JP')}</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>出价后将立即提交到拍卖行，无法撤回</div>
        </div>
      ),
      okText: '确认出价',
      cancelText: '取消',
      onOk: async () => {
        try {
          const { data } = await api.patch(`/mandates/${mandate.id}/submit-bid`);
          message.success(data.customer_message || '出价已提交');
          fetchMandates();
        } catch (e) {
          message.error(e?.response?.data?.customer_message || e?.response?.data?.error || '出价失败');
        }
      },
    });
  };

  const openLot = (lotId) => {
    navigate(`/lots/${lotId}`);
  };

  const countItems = [
    { key: 'active',   label: '竞拍中', value: counts.active,   color: BLUE },
    { key: 'watching', label: '待出价', value: counts.watching, color: GOLD },
    { key: 'outbid',   label: '已被超', value: counts.outbid,   color: RED },
    { key: 'won',      label: '已中标', value: counts.won,      color: GRN },
    { key: 'unwon',    label: '未中标', value: counts.unwon,    color: INK4 },
  ];

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '0 0 60px' : '20px 24px 60px' }}>
      {/* 标题 */}
      <div style={{ padding: isMobile ? '16px 14px 0' : '0 0 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: INK, letterSpacing: 1 }}>竞拍中心</div>
        <div style={{ fontSize: 11, color: INK4, marginTop: 4 }}>实时查看竞拍状态，即时调整出价</div>
      </div>

      {/* 状态统计 */}
      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${countItems.length},1fr)`, gap: 0,
        margin: isMobile ? '12px 10px' : '0 0 16px',
        background: '#fff', borderRadius: 10, border: `1px solid ${BD2}`, overflow: 'hidden',
      }}>
        {countItems.map((item, i) => (
          <div
            key={item.key}
            onClick={() => setTab(item.key)}
            style={{
              padding: '14px 8px', textAlign: 'center', cursor: 'pointer',
              background: tab === item.key ? 'rgba(184,137,42,.06)' : 'transparent',
              borderRight: i < countItems.length - 1 ? `1px solid ${BD2}` : 'none',
              transition: 'background .12s',
            }}
          >
            <div style={{
              fontFamily: 'var(--mono,monospace)', fontSize: 22, fontWeight: 600,
              color: item.value > 0 ? item.color : INK4,
            }}>
              {item.value}
            </div>
            <div style={{ fontSize: 11, color: tab === item.key ? GOLD : INK4, fontWeight: tab === item.key ? 600 : 400 }}>
              {item.label}
            </div>
          </div>
        ))}
      </div>

      {/* 排序栏 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0,
        margin: isMobile ? '0 10px 10px' : '0 0 12px',
        background: '#fff', borderRadius: 8, border: `1px solid ${BD2}`, overflow: 'hidden',
      }}>
        {SORT_OPTIONS.map((opt, i) => (
          <button
            key={opt.key}
            onClick={() => setSort(opt.key)}
            style={{
              flex: 1, padding: '9px 8px', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: sort === opt.key ? 600 : 400,
              color: sort === opt.key ? GOLD : INK3,
              background: sort === opt.key ? 'rgba(184,137,42,.06)' : 'transparent',
              borderRight: i < SORT_OPTIONS.length - 1 ? `1px solid ${BD2}` : 'none',
              transition: 'all .12s',
            }}
          >
            {opt.label} {sort === opt.key ? (opt.key.includes('asc') ? '↑' : '↓') : ''}
          </button>
        ))}
        <button
          onClick={fetchMandates}
          style={{
            padding: '9px 14px', border: 'none', cursor: 'pointer',
            fontSize: 12, color: INK3, background: 'transparent',
            borderLeft: `1px solid ${BD2}`,
          }}
        >
          刷新
        </button>
      </div>

      {/* 列表 */}
      <div style={{ padding: isMobile ? '0 10px' : 0 }}>
        <Spin spinning={loading}>
          {tab === 'watching' ? (
            // 待出价 tab: 待审核委托 + 关注中拍品
            (mandates.length === 0 && watchlist.length === 0 && !loading) ? (
              <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 60, marginBottom: 16, opacity: 0.3 }}>🛒</div>
                <div style={{ fontSize: 16, color: INK3, marginBottom: 8 }}>暂无待出价商品</div>
                <div
                  onClick={() => navigate('/auction-hub')}
                  style={{ fontSize: 14, color: GOLD, cursor: 'pointer' }}
                >
                  去浏览拍品，关注心仪商品 →
                </div>
              </div>
            ) : (
              <>
                {mandates.length > 0 && (
                  <div style={{ fontSize: 11, color: INK4, padding: '8px 4px 4px', fontWeight: 600 }}>
                    待审核委托（{mandates.length}）
                  </div>
                )}
                {mandates.map(m => (
                  <BidCard
                    key={m.id}
                    mandate={m}
                    onAdjust={openAdjust}
                    onOpenLot={openLot}
                    onManualSubmit={handleManualSubmit}
                    isMobile={isMobile}
                  />
                ))}
                {watchlist.length > 0 && (
                  <div style={{ fontSize: 11, color: INK4, padding: '8px 4px 4px', fontWeight: 600 }}>
                    关注中拍品（{watchlist.length}）
                  </div>
                )}
                {watchlist.map(w => (
                  <WatchlistCard
                    key={w.watchlist_id || w.id}
                    lot={w}
                    onOpenLot={openLot}
                    onBid={(lotId) => navigate(`/lots/${lotId}`)}
                    isMobile={isMobile}
                  />
                ))}
              </>
            )
          ) : (
            // 其他 tab: 正常委托列表
            mandates.length === 0 && !loading ? (
              <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 60, marginBottom: 16, opacity: 0.3 }}>🛒</div>
                <div style={{ fontSize: 16, color: INK3, marginBottom: 8 }}>暂无商品</div>
                <div
                  onClick={() => navigate('/auction-hub')}
                  style={{ fontSize: 14, color: GOLD, cursor: 'pointer' }}
                >
                  去浏览拍品 →
                </div>
              </div>
            ) : (
              mandates.map(m => (
                <BidCard
                  key={m.id}
                  mandate={m}
                  onAdjust={openAdjust}
                  onOpenLot={openLot}
                  isMobile={isMobile}
                />
              ))
            )
          )}
        </Spin>
      </div>

      {/* 加价弹窗 */}
      <Modal
        title={adjustTarget?.status === 'bid_failed' || adjustTarget?.bid_error_code ? '调整出价重新委托' : '调整出价'}
        open={!!adjustTarget}
        onOk={handleAdjust}
        onCancel={() => { setAdjustTarget(null); setAdjustValue(''); setAdjustMinBid(null); }}
        okText={adjustTarget?.status === 'bid_failed' || adjustTarget?.bid_error_code ? '确认重新委托' : '确认加价'}
        cancelText="取消"
        confirmLoading={adjusting}
        width={400}
        destroyOnClose
      >
        {adjustTarget && (
          <div style={{ paddingTop: 8 }}>
            <div style={{ fontSize: 13, color: INK3, marginBottom: 12, lineHeight: 1.6 }}>
              {adjustTarget.title_zh || adjustTarget.title_ja}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div style={{ background: BG, borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: INK4 }}>当前价格</div>
                <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 16, fontWeight: 600, color: INK }}>
                  ¥{Number(adjustTarget.lot_current_price || 0).toLocaleString('ja-JP')}
                </div>
              </div>
              <div style={{ background: BG, borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: INK4 }}>我的当前出价</div>
                <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 16, fontWeight: 600, color: GOLD }}>
                  ¥{Number(adjustTarget.max_bid_jpy).toLocaleString('ja-JP')}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: INK3, marginBottom: 6 }}>
                新出价金额（日元）
                {adjustMinBid > 0 && (
                  <span style={{ marginLeft: 8, color: BID_WARN, fontWeight: 600 }}>
                    最低需 ¥{Number(adjustMinBid).toLocaleString('ja-JP')}
                  </span>
                )}
              </div>
              <input
                type="number"
                value={adjustValue}
                onChange={e => setAdjustValue(e.target.value)}
                placeholder={adjustMinBid > 0
                  ? `最低 ¥${Number(adjustMinBid).toLocaleString('ja-JP')}`
                  : `最低 ¥${(Number(adjustTarget.max_bid_jpy) + 1000).toLocaleString()}`}
                autoFocus
                style={{
                  width: '100%', padding: '12px 14px', fontSize: 18, fontWeight: 500,
                  border: `2px solid ${BD}`, borderRadius: 6, outline: 'none',
                  fontFamily: 'var(--mono,monospace)', textAlign: 'right',
                  boxSizing: 'border-box',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = GOLD; }}
                onBlur={e => { e.currentTarget.style.borderColor = BD; }}
              />
            </div>

            {/* 快捷加价 */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {[1000, 5000, 10000, 50000, 100000].map(step => (
                <button
                  key={step}
                  onClick={() => {
                    const base = Number(adjustValue) || Number(adjustTarget.max_bid_jpy);
                    setAdjustValue(String(base + step));
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: 5, fontSize: 12,
                    background: '#fff', color: GOLD, border: `1px solid ${BD}`,
                    cursor: 'pointer', fontFamily: 'var(--mono,monospace)',
                  }}
                >
                  +{step >= 10000 ? `${step / 10000}万` : step.toLocaleString()}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 11, color: INK4, lineHeight: 1.6 }}>
              {adjustTarget.status === 'bid_failed' ? (
                <>* 确认后将自动重新出价，保证金按新出价重新冻结</>
              ) : (
                <>* 加价后可能需要追加冻结保证金<br />* 出价只能增加，不能降低</>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
